import { setLocale } from '@/plugins/i18n';
import { createWebHistory, type Router, type RouterOptions } from 'vue-router';
import { routes } from '@/plugins/router/routes.ts';
import { useRouteStore } from '@/plugins/pinia/routeStore.ts';
import { useGoogleTagManager } from '@/plugins/analytics/google-tag-manager';
import { Constants } from '@/utilities/constants.ts';

// Seules ces ancres déclenchent un défilement. Toutes les autres continuent d'être ignorées,
// car la page de projet se sert du hash comme état de sélection de modèle.
const scrollableAnchors: readonly string[] = Object.values(Constants.ScrollAnchors).map((anchor) => `#${anchor}`);

// La section visée est rendue après le chargement asynchrone de ses données,
// donc elle n'existe pas encore au moment où le routeur calcule le défilement.
const waitForElement = (selector: string, timeoutMs: number): Promise<Element | null> =>
  new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector);
      if (element) {
        observer.disconnect();
        clearTimeout(timeoutId);
        resolve(element);
      }
    });

    const timeoutId = setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeoutMs);

    observer.observe(document.body, { childList: true, subtree: true });
  });

export const routerOptions = import.meta.env.SSR
  ? {
      routes,
    }
  : ({
      routes,
      history: createWebHistory(),
      async scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
          return savedPosition;
        }
        if (to.path === from.path && to.hash !== from.hash) {
          // Keep the current scroll position when only the hash changes
          return false;
        }
        if (to.hash && scrollableAnchors.includes(to.hash) && (await waitForElement(to.hash, 3000))) {
          return { el: to.hash, top: 16 };
        }
        return { top: 0 };
      },
    } as RouterOptions);

export const configureRouter = (router: Router) => {
  router.beforeEach(setLocale);

  router.afterEach((to, from) => {
    const ga = useGoogleTagManager();
    if (ga) {
      ga.trackPageView(to.fullPath, document.title);
    }
    const routeStore = useRouteStore();
    routeStore.setLastRoute(from);
  });
};
